import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import leaf from "../../Assets/Images/leaf.png";
import { Sparkles, ChevronRight } from "lucide-react";
import { getCloudinaryImageUrl } from "../../utils/getCloudinaryImageUrl";

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);
  const navigate = useNavigate();

  const fetchCategories = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/service-category/get-all`
      );

      if (res.data?.success) {
        setCategories(res.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch categories", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCategoryClick = (categoryId, index) => {
    const card = document.getElementById(`category-${index}`);
    if (card) {
      card.classList.add("active-click");
      setTimeout(() => card.classList.remove("active-click"), 300);
    }

    navigate(`services/${categoryId}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section className="relative bg-gradient-to-b from-[#FFFAF9] to-white px-4 sm:px-8 md:px-10 lg:px-12 mx-auto py-12 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-[#ab310c]/5 to-transparent rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-[#EDB5A4]/10 to-transparent rounded-full translate-x-1/3 translate-y-1/3 blur-3xl" />

      <div className="relative z-10">
        <div className="text-center flex flex-col gap-y-6 max-w-4xl mx-auto mb-12">
          {/* Category Badge with enhanced styling */}
          <div className="relative mx-auto w-fit">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#ab310c] to-[#EDB5A4] rounded-full blur opacity-20 group-hover:opacity-30 transition duration-1000 group-hover:duration-200 animate-pulse" />
            <div className="relative uppercase font-robotoflex flex items-center gap-2 sm:gap-3 border-2 border-white/30 bg-white/90 backdrop-blur-sm rounded-full text-[#ab310c] px-4 sm:px-6 py-1.5 text-sm sm:text-base tracking-wide font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group">
              <img
                src={leaf}
                alt="leaf"
                className="w-5 h-5 sm:w-6 sm:h-6 transition-transform group-hover:rotate-12 duration-300"
              />
              <span className="bg-gradient-to-r from-[#ab310c] to-[#8a290a] bg-clip-text text-transparent">
                Categories
              </span>
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 ml-1" />
            </div>
          </div>

          {/* Heading with gradient text */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-philosopher font-bold text-gray-900 leading-tight tracking-tight">
            <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
              Your beauty,&nbsp;
            </span>
            {/* <br /> */}
            <span className="relative">
              <span className="bg-gradient-to-r from-[#ab310c] via-[#d44a20] to-[#EDB5A4] bg-clip-text text-transparent">
                simplified
              </span>
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-[#ab310c] to-[#EDB5A4] rounded-full" />
            </span>
          </h1>

          {/* Description with better typography */}
          <p className="font-robotoflex text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed tracking-wide max-w-3xl mx-auto px-4">
            Discover personalized beauty services tailored just for you.
            <span className="text-[#ab310c] font-medium">
              {" "}
              Sakhi Salon
            </span>{" "}
            offers everything you need to feel confident, radiant, and
            beautifully you.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6 md:gap-8 relative">
          {loading
            ? Array.from({ length: 10 }).map((_, i) => (
              <CategoryCardSkeleton key={i} index={i} />
            ))
            : categories.map((item, index) => (
              <CategoryCard
                key={item._id}
                id={`category-${index}`}
                index={index}
                imageUrl={item.imageUrl}
                name={item.categoryName}
                onClick={() => handleCategoryClick(item._id, index)}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                isHovered={hoveredCard === index}
              />
            ))}
        </div>

        {/* View All Button for mobile */}
        {categories.length > 10 && (
          <div className="mt-12 text-center md:hidden">
            <button className="group inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#ab310c] to-[#d44a20] text-white rounded-full hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-300 font-semibold shadow-lg">
              View All Categories
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

const CategoryCardSkeleton = ({ index }) => {
  return (
    <div
      data-aos="fade-up"
      data-aos-delay={index * 50}
      className="flex flex-col items-center gap-y-3 lg:gap-y-4"
    >
      <div className="relative">
        <div className="size-[8rem] sm:size-[10rem] md:size-[11rem] lg:size-[12rem] xl:size-[14rem] bg-gradient-to-br from-gray-200 via-gray-300 to-gray-200 rounded-full animate-pulse" />
        <div className="absolute inset-0 rounded-full bg-gradient-to-t from-transparent via-white/10 to-transparent" />
      </div>
      <div className="h-4 sm:h-5 w-20 sm:w-24 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full animate-pulse" />
    </div>
  );
};

const CategoryCard = ({
  id,
  imageUrl,
  name,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isHovered,
  index,
}) => {
  return (
    <div
      id={id}
      data-aos="fade-up"
      data-aos-delay={index * 50}
      data-aos-duration="600"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      className="group relative flex flex-col items-center gap-y-3 lg:gap-y-4 cursor-pointer"
    >
      {/* Glow effect on hover */}
      <div
        className={`absolute -inset-2 bg-gradient-to-r from-[#ab310c]/20 to-[#EDB5A4]/20 rounded-full blur-xl transition-all duration-500 ${isHovered ? "opacity-100 scale-105" : "opacity-0 scale-95"
          }`}
      />

      {/* Card container */}
      <div className="relative">
        {/* Image container with gradient border */}
        <div
          className={`absolute -inset-0.5 bg-gradient-to-r from-[#ab310c] via-[#d44a20] to-[#EDB5A4] rounded-full transition-all duration-500 ${isHovered ? "opacity-100" : "opacity-0"
            }`}
        />

        {/* Image with hover effect */}
        <div className="relative size-[8rem] sm:size-[10rem] md:size-[11rem] lg:size-[12rem] xl:size-[14rem] overflow-hidden rounded-full border-4 border-[#fffcfb] bg-[#fffcfb]  group-hover:shadow-2xl transition-all duration-500 group-hover:scale-110 group-active:scale-105">
          <img
            src={getCloudinaryImageUrl(imageUrl, { width: 400 })}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full" />

          {/* View indicator */}
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-gray-800 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300">
            View Services
          </div>
        </div>

        {/* Floating icon on hover */}
        <div
          className={`absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-[#ab310c] to-[#d44a20] rounded-full flex items-center justify-center text-white shadow-lg transition-all duration-300 ${isHovered
            ? "opacity-100 scale-100 rotate-0"
            : "opacity-0 scale-0 rotate-45"
            }`}
        >
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>

      {/* Category name with hover effect */}
      <p className="font-philosopher text-center text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-800 group-hover:text-[#ab310c] transition-colors duration-300 relative">
        {name}
        <span
          className={`absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-0 group-hover:w-8 h-0.5 bg-gradient-to-r from-[#ab310c] to-[#EDB5A4] rounded-full transition-all duration-300 ${isHovered ? "opacity-100" : "opacity-0"
            }`}
        />
      </p>
    </div>
  );
};

// Add CSS for click animation
const styles = `
  @keyframes cardClick {
    0% { transform: scale(1); }
    50% { transform: scale(0.95); }
    100% { transform: scale(1); }
  }
  .active-click {
    animation: cardClick 0.3s ease;
  }
`;

// Add styles to document
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

export default Categories;